/**
 * LabCoverageHeatMap — iter755 (Feb 2026)
 *
 * Visual heat-map of all 44+ canonical lab markers. Each marker is
 * coloured by state (covered/available/not_relevant) and grouped by
 * clinical category. Includes a "Next 3 markers to add" recommendation.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import {
  Grid3x3, CheckCircle2, Circle, MinusCircle, Sparkles, Loader2, Zap,
} from "lucide-react";
import { influencesFor } from "./markerInfluenceMap";

// Tailwind chip colour map — keyed by MODULE_META.color
const CHIP_COLORS = {
  pink:     { light: "bg-pink-100 text-pink-800 border-pink-200",         dark: "bg-pink-500/15 text-pink-200 border-pink-500/30" },
  fuchsia:  { light: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200", dark: "bg-fuchsia-500/15 text-fuchsia-200 border-fuchsia-500/30" },
  purple:   { light: "bg-purple-100 text-purple-800 border-purple-200",   dark: "bg-purple-500/15 text-purple-200 border-purple-500/30" },
  violet:   { light: "bg-violet-100 text-violet-800 border-violet-200",   dark: "bg-violet-500/15 text-violet-200 border-violet-500/30" },
  amber:    { light: "bg-amber-100 text-amber-800 border-amber-200",      dark: "bg-amber-500/15 text-amber-200 border-amber-500/30" },
  rose:     { light: "bg-rose-100 text-rose-800 border-rose-200",         dark: "bg-rose-500/15 text-rose-200 border-rose-500/30" },
  orange:   { light: "bg-orange-100 text-orange-800 border-orange-200",   dark: "bg-orange-500/15 text-orange-200 border-orange-500/30" },
  sky:      { light: "bg-sky-100 text-sky-800 border-sky-200",            dark: "bg-sky-500/15 text-sky-200 border-sky-500/30" },
  cyan:     { light: "bg-cyan-100 text-cyan-800 border-cyan-200",         dark: "bg-cyan-500/15 text-cyan-200 border-cyan-500/30" },
  emerald:  { light: "bg-emerald-100 text-emerald-800 border-emerald-200",dark: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30" },
  indigo:   { light: "bg-indigo-100 text-indigo-800 border-indigo-200",   dark: "bg-indigo-500/15 text-indigo-200 border-indigo-500/30" },
  red:      { light: "bg-red-100 text-red-800 border-red-200",            dark: "bg-red-500/15 text-red-200 border-red-500/30" },
  teal:     { light: "bg-teal-100 text-teal-800 border-teal-200",         dark: "bg-teal-500/15 text-teal-200 border-teal-500/30" },
  blue:     { light: "bg-blue-100 text-blue-800 border-blue-200",         dark: "bg-blue-500/15 text-blue-200 border-blue-500/30" },
};

const STATE_STYLE = {
  covered: {
    bg_light: "bg-emerald-500", bg_dark: "bg-emerald-500",
    text_light: "text-white", text_dark: "text-white",
    border_light: "border-emerald-600", border_dark: "border-emerald-400",
    icon: CheckCircle2, label_bg: "Имам",
  },
  available: {
    bg_light: "bg-amber-100", bg_dark: "bg-amber-900/40",
    text_light: "text-amber-900", text_dark: "text-amber-200",
    border_light: "border-amber-300", border_dark: "border-amber-700",
    icon: Circle, label_bg: "Мога да добавя",
  },
  not_relevant: {
    bg_light: "bg-slate-200", bg_dark: "bg-slate-800/60",
    text_light: "text-slate-500", text_dark: "text-slate-500",
    border_light: "border-slate-300", border_dark: "border-slate-700",
    icon: MinusCircle, label_bg: "Не се отнася",
  },
};

export default function LabCoverageHeatMap() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/labs/coverage`);
      setData(d);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className={`p-6 rounded-2xl border flex items-center justify-center min-h-[200px] ${
        isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200"
      }`}>
        <Loader2 className="w-5 h-5 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (!data) return null;

  const s = data.summary || {};
  const cardCls = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";

  return (
    <section
      data-testid="lab-coverage-heatmap"
      className={`p-5 rounded-2xl border ${cardCls}`}
    >
      {/* Header */}
      <div className="flex items-start gap-3 mb-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
          isDark ? "bg-violet-900/60" : "bg-violet-100"
        }`}>
          <Grid3x3 className="w-5 h-5 text-violet-500" />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-[10px] uppercase font-bold tracking-widest mb-0.5 ${
            isDark ? "text-violet-300" : "text-violet-600"
          }`}>
            Покритие на лабораторни изследвания
          </div>
          <h2 className={`text-base font-semibold ${isDark ? "text-slate-100" : "text-slate-900"}`}>
            Покритие на лабораторни маркери
          </h2>
          <p className={`text-[12px] leading-relaxed mt-1 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
            Всичките {s.total_markers ?? 0} канонични маркера от библиотеката на Nutramilo,
            групирани по клинична категория. Всеки цвят показва състоянието спрямо Вашите данни.
          </p>
        </div>
      </div>

      {/* Coverage bar */}
      <div className={`p-4 rounded-xl mb-4 ${isDark ? "bg-slate-950/60" : "bg-slate-50"}`}>
        <div className="flex items-baseline justify-between mb-2">
          <span className={`text-[10px] uppercase font-bold ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            Общо покритие (релевантни маркери)
          </span>
          <span className={`text-2xl font-bold ${isDark ? "text-emerald-300" : "text-emerald-700"}`}
                data-testid="lab-coverage-pct">
            {s.coverage_pct ?? 0}%
          </span>
        </div>
        <div className={`h-2 rounded-full overflow-hidden ${isDark ? "bg-slate-800" : "bg-slate-200"}`}>
          <div
            className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all"
            style={{ width: `${s.coverage_pct ?? 0}%` }}
          />
        </div>
        <div className="flex gap-3 mt-3 text-[11px]">
          <span className={`flex items-center gap-1 ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
            <span className="w-2 h-2 rounded-full bg-emerald-500" /> {s.covered ?? 0} имам
          </span>
          <span className={`flex items-center gap-1 ${isDark ? "text-amber-300" : "text-amber-700"}`}>
            <span className="w-2 h-2 rounded-full bg-amber-500" /> {s.available ?? 0} мога
          </span>
          <span className={`flex items-center gap-1 ${isDark ? "text-slate-500" : "text-slate-500"}`}>
            <span className="w-2 h-2 rounded-full bg-slate-400" /> {s.not_relevant ?? 0} нерелевантни
          </span>
        </div>
      </div>

      {/* Next up recommendations */}
      {data.next_up?.length > 0 && (
        <div
          data-testid="lab-coverage-next-up"
          className={`p-3 rounded-xl mb-4 border-l-4 ${
            isDark
              ? "bg-amber-500/5 border-amber-400"
              : "bg-amber-50 border-amber-500"
          }`}
        >
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className={`w-4 h-4 ${isDark ? "text-amber-300" : "text-amber-600"}`} />
            <span className={`text-[11px] font-bold uppercase tracking-wide ${
              isDark ? "text-amber-200" : "text-amber-900"
            }`}>
              Топ 3 маркера за следваща стъпка
            </span>
          </div>
          <div className="space-y-1.5">
            {data.next_up.map((n, i) => (
              <div key={n.code} className="flex items-center gap-2 text-[12px]">
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                  isDark ? "bg-amber-500/30 text-amber-200" : "bg-amber-200 text-amber-900"
                }`}>
                  {i + 1}
                </span>
                <span className={`flex-1 font-medium ${isDark ? "text-amber-100" : "text-amber-900"}`}>
                  {n.label_bg}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Categories grid */}
      <div className="space-y-4">
        {(data.categories || []).map((cat) => (
          <div key={cat.id} data-testid={`lab-cat-${cat.id}`}>
            <div className="flex items-baseline justify-between mb-1.5">
              <span className={`text-[12px] font-semibold ${isDark ? "text-slate-200" : "text-slate-800"}`}>
                {cat.label_bg}
              </span>
              <span className={`text-[10px] tabular-nums ${
                cat.coverage_pct >= 60
                  ? (isDark ? "text-emerald-400" : "text-emerald-700")
                  : cat.coverage_pct >= 30
                    ? (isDark ? "text-amber-400" : "text-amber-700")
                    : (isDark ? "text-slate-500" : "text-slate-500")
              }`}>
                {cat.covered}/{cat.relevant_total} · {cat.coverage_pct}%
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1.5">
              {cat.markers.map((m) => {
                const st = STATE_STYLE[m.state] || STATE_STYLE.available;
                const Icon = st.icon;
                const bgCls = isDark ? st.bg_dark : st.bg_light;
                const textCls = isDark ? st.text_dark : st.text_light;
                const borderCls = isDark ? st.border_dark : st.border_light;
                const infCount = (influencesFor(m.code) || []).length;
                return (
                  <button
                    key={m.code}
                    onClick={() => setSelected(selected?.code === m.code ? null : m)}
                    data-testid={`lab-marker-${m.code}`}
                    className={`px-2 py-1.5 rounded-lg border text-left flex items-center gap-1.5 transition-all hover:shadow ${bgCls} ${textCls} ${borderCls}`}
                    title={`${m.label_bg} — ${st.label_bg}${infCount ? ` · влияе на ${infCount} модула` : ""}`}
                  >
                    <Icon className="w-3 h-3 flex-shrink-0" />
                    <span className="text-[11px] truncate flex-1">{m.label_bg}</span>
                    {infCount > 0 && (
                      <span
                        className={`shrink-0 inline-flex items-center gap-0.5 text-[9px] font-bold px-1 rounded ${
                          m.state === "covered"
                            ? "bg-white/25 text-white"
                            : (isDark ? "bg-violet-500/25 text-violet-200" : "bg-violet-100 text-violet-700")
                        }`}
                        data-testid={`lab-marker-inf-count-${m.code}`}
                      >
                        <Zap className="w-2.5 h-2.5" />
                        {infCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Selected marker detail */}
      {selected && (
        <div
          data-testid="lab-marker-detail"
          className={`mt-4 p-3 rounded-xl border ${
            isDark ? "bg-slate-950/70 border-slate-700" : "bg-slate-50 border-slate-200"
          }`}
        >
          <div className={`text-sm font-semibold ${isDark ? "text-slate-100" : "text-slate-900"}`}>
            {selected.label_bg}
            {selected.unit && (
              <span className={`ml-2 text-[11px] font-mono opacity-70`}>({selected.unit})</span>
            )}
          </div>
          <div className={`mt-1 text-[11px] flex items-center gap-2`}>
            <span
              className={`px-1.5 py-0.5 rounded font-semibold ${
                selected.state === "covered"
                  ? "bg-emerald-100 text-emerald-800"
                  : selected.state === "available"
                    ? "bg-amber-100 text-amber-800"
                    : "bg-slate-200 text-slate-600"
              }`}
            >
              {STATE_STYLE[selected.state].label_bg}
            </span>
          </div>
          {selected.state === "available" && (
            <p className={`mt-2 text-[11px] leading-relaxed ${isDark ? "text-slate-400" : "text-slate-600"}`}>
              Този маркер е релевантен за Вашия пол/възраст, но все още няма данни в lab_entries.
              Качете лабораторен резултат или го добавете ръчно, за да го активирате в Digital Twins.
            </p>
          )}

          {/* iter882 — Influence chips: which BHI/Twin/KEGG modules consume this marker */}
          {(() => {
            const inf = influencesFor(selected.code);
            if (!inf.length) return null;
            return (
              <div className="mt-3" data-testid={`lab-marker-influences-${selected.code}`}>
                <div className={`flex items-center gap-1.5 mb-1.5 text-[10px] uppercase font-bold tracking-wide ${
                  isDark ? "text-violet-300" : "text-violet-700"
                }`}>
                  <Zap className="w-3 h-3" />
                  Влияе на {inf.length} модула
                </div>
                <div className="flex flex-wrap gap-1">
                  {inf.map((chip) => {
                    const cc = CHIP_COLORS[chip.color] || CHIP_COLORS.purple;
                    const cls = isDark ? cc.dark : cc.light;
                    return (
                      <span
                        key={chip.key}
                        className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-semibold ${cls}`}
                        data-testid={`lab-marker-chip-${selected.code}-${chip.key}`}
                      >
                        {chip.label_bg}
                      </span>
                    );
                  })}
                </div>
                <p className={`mt-2 text-[10px] italic leading-relaxed ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                  Каноничните стойности се overlay-ват върху горните модули през PatientHealthContext (iter702 Phase 3).
                </p>
              </div>
            );
          })()}
        </div>
      )}
    </section>
  );
}
